import React from 'react';
import { useAsaasPlanHistory } from '@/hooks/useAsaasPlanHistory';
import { Badge } from '@/components/ui/badge';
import { Clock, TrendingUp, TrendingDown, AlertTriangle, CreditCard, CheckCircle } from 'lucide-react';

// Função para traduzir status para português
const formatStatus = (status: string) => {
  const statusMap: Record<string, string> = {
    'active': 'Ativo',
    'scheduled': 'Agendado',
    'superseded': 'Substituído',
    'expired': 'Expirado',
    'cancelled': 'Cancelado'
  };
  return statusMap[status] || status;
};

export const SubscriptionHistorySection = () => {
  const { planHistory, hasHistory, isLoading } = useAsaasPlanHistory();

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-20 bg-white/10 rounded-xl"></div>
        <div className="h-20 bg-white/10 rounded-xl"></div>
      </div>
    );
  }

  // Estado vazio - nenhum histórico
  if (!hasHistory) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
        <Clock className="h-12 w-12 text-gray-500 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-white mb-2">
          Nenhum histórico ainda
        </h4>
        <p className="text-gray-400 text-sm">
          Quando você fizer mudanças no seu plano, elas aparecerão aqui.
        </p>
      </div>
    );
  }

  // Mostrar timeline de mudanças
  return (
    <div className="space-y-3">
      {planHistory.map((change) => {
        const isUpgrade = change.old_plan && change.new_plan > change.old_plan;
        const isDowngrade = change.old_plan && change.new_plan < change.old_plan;
        const isCancellation = change.type === 'cancellation' || change.payment_method_type === 'cancellation';
        
        return (
          <div key={change.id} className="bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm overflow-hidden">
            {/* Header */}
            <div className="p-4 pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    isCancellation ? 'bg-red-500/20 text-red-400' :
                    isUpgrade ? 'bg-green-500/20 text-green-400' : 
                    isDowngrade ? 'bg-red-500/20 text-red-400' : 
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {isCancellation ? <AlertTriangle className="h-5 w-5" /> :
                     isUpgrade ? <TrendingUp className="h-5 w-5" /> : 
                     isDowngrade ? <TrendingDown className="h-5 w-5" /> : 
                     <Clock className="h-5 w-5" />}
                  </div>
                  
                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <h5 className="text-white font-medium text-sm leading-tight mb-1">
                      {isCancellation ? 
                        `Cancelamento agendado` :
                        `${change.old_plan_formatted} → ${change.new_plan_formatted}`
                      }
                    </h5>
                    <div className="text-xs text-gray-400">
                      {change.created_at_formatted}
                    </div>
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline" className="text-xs border-white/30 text-gray-300 whitespace-nowrap">
                    {formatStatus(change.status)}
                  </Badge>
                  {change.status !== 'superseded' && (
                    <Badge variant="outline" className="text-xs border-green-500/30 text-green-400 bg-green-500/10">
                      Processado
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="px-4 pb-4">
              {/* Financial Info Row */}
              <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                <div className="bg-white/5 rounded-lg p-2">
                  <div className="text-gray-400 mb-1">Valor</div>
                  <div className="text-white font-medium">{change.amount_formatted}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-2">
                  <div className="text-gray-400 mb-1">Período</div>
                  <div className="text-white font-medium">{change.billing_period_formatted}</div>
                </div>
              </div>

              {/* Payment Details - Only if exists */}
              {(change.payment_method_formatted || change.payment_status_formatted) && (
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-3 w-3 text-luxury-pinkBright" />
                    <span className="text-xs font-medium text-luxury-pinkBright">
                      {isCancellation ? 'Detalhes do Cancelamento' : 'Detalhes do Pagamento'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    {/* Payment Method */}
                    {(change.payment_method_formatted && change.payment_method_formatted !== 'Não informado') && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Método:</span>
                        <span className="text-gray-300 text-right">{change.payment_method_formatted}</span>
                      </div>
                    )}
                    
                    {/* Payment Status */}
                    {(change.payment_status_formatted && change.payment_status_formatted !== 'Não informado') && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Status:</span>
                        <div className="flex items-center gap-1">
                          <CheckCircle className={`h-3 w-3 ${
                            change.payment_status_formatted === 'Pago' ? 'text-green-400' : 
                            change.payment_status_formatted === 'Pendente' || change.payment_status_formatted === 'scheduled' ? 'text-yellow-400' : 
                            'text-red-400'
                          }`} />
                          <span className={`text-right ${
                            change.payment_status_formatted === 'Pago' ? 'text-green-400' : 
                            change.payment_status_formatted === 'Pendente' || change.payment_status_formatted === 'scheduled' ? 'text-yellow-400' : 
                            'text-red-400'
                          }`}>
                            {change.payment_status_formatted === 'scheduled' ? 'Agendado' : change.payment_status_formatted}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Effective Date for scheduled changes */}
                    {change.effective_date_formatted && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Data Efetiva:</span>
                        <span className="text-gray-300 text-right">{change.effective_date_formatted}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
